import { supabase } from './supabaseClient';
import { resolveApiBaseUrl } from './apiBaseUrl';

const API_BASE_URL = resolveApiBaseUrl();
const API_TIMEOUT_MS = 15000;

class UnauthorizedError extends Error {
  constructor(message) {
    super(message);
    this.name = 'UnauthorizedError';
    this.status = 401;
  }
}

export const apiClient = async (endpoint, options = {}) => {
  const { data: { session } } = await supabase.auth.getSession();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

  const headers = {
    'Content-Type': 'application/json',
    ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
    ...options.headers,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    if (response.status === 401) {
      throw new UnauthorizedError('Session expired or invalid');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.detail || 'API request failed');
      error.status = response.status;
      error.response = { data: errorData, status: response.status };
      throw error;
    }

    return response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timed out. Please try again.');
      timeoutError.status = 408;
      timeoutError.response = {
        data: { detail: 'Request timed out. Please try again.' },
        status: 408,
      };
      throw timeoutError;
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const getFeed = () => {
  return apiClient('/api/feed');
};

export const getMonitors = () => {
  return apiClient('/api/monitors');
};

export const getReportsForMonitor = (monitorId) => {
  if (!monitorId) {
    return Promise.reject(new Error('monitorId is required'));
  }
  const encodedMonitorId = encodeURIComponent(monitorId);
  return apiClient(`/api/monitors/${encodedMonitorId}/reports`);
};

export const createMonitor = (data) => {
  return apiClient('/api/monitors', {
    method: 'POST',
    body: JSON.stringify({
      term: data.term,
      frequency: data.frequency,
    }),
  });
};

export const quickScan = (term) => {
  return apiClient('/api/quick-scan', {
    method: 'POST',
    body: JSON.stringify({ term }),
  });
};



export const downloadReport = async (reportId) => {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('No active session');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/reports/${reportId}/download`, {
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
      },
      redirect: 'follow',
    });

    if (response.status === 401) {
      throw new Error('Session expired or invalid');
    }

    if (!response.ok) {
      throw new Error('Failed to download report');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `threat-report-${reportId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};
